/**
 * Application Constants
 * 
 * Centralized configuration and magic numbers used throughout the application.
 * Organized by feature/domain for easy discovery and maintenance.
 * 
 * Phase 1 (High-impact): FORM, ERROR, FIRESTORE
 * Phases 2+: UI_COLORS, CLOUD_FUNCTIONS, CACHE, I18N_KEYS, etc.
 */

/**
 * Form validation and UI constants
 */
export const FORM = {
  // Idea form
  MAX_IDEA_DESCRIPTION_WORDS: 300,
  SPINNER_CLASS: 'idea-form-spinner ml-2 inline-block w-4 h-4 border-2 border-t-2 border-blue-500 border-t-transparent rounded-full animate-spin',
  
  // CSS color classes for form messages
  MESSAGE_COLORS: {
    ERROR: 'text-red-600',
    WARNING: 'text-yellow-600',
    INFO: 'text-blue-600',
    SUCCESS: 'text-green-600'
  }
};

/**
 * Error handling and logging constants
 */
export const ERROR = {
  // Severity levels
  SEVERITY: {
    CRITICAL: 'critical',
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info'
  },
  
  // Common Firebase error codes
  CODE: {
    UNAUTHENTICATED: 'unauthenticated',
    PERMISSION_DENIED: 'permission-denied'
  },
  
  // Error messages
  MESSAGES: {
    AI_VERIFICATION_FAILED: 'AI verification failed. Please try again later.',
    AUTH_REQUIRED: 'Please log in to submit ideas.',
    APP_CHECK_FAILED: 'App Check failed. Try again from a real device.'
  },
  
  // Error queue configuration
  MAX_ERROR_QUEUE_SIZE: 10,
  
  // Popup modal titles
  MODAL_TITLE: {
    SUBMISSION_ERROR: 'Submission Error'
  }
};

/**
 * Firestore database constants
 */
export const FIRESTORE = {
  // Collection names
  COLLECTIONS: {
    IDEAS_EN: 'ideas_en',
    IDEAS_PL: 'ideas_pl',
    IDEAS_EUEN: 'ideas_euen'
  },
  
  // Query defaults
  QUERY: {
    RECENT_IDEAS_LIMIT: 3,
    ORDER_BY_FIELD: 'createdAt',
    ORDER_DIRECTION: 'desc'
  },
  
  // Document field names
  IDEA_FIELDS: {
    TITLE: 'title',
    DESCRIPTION: 'description',
    CREATED_AT: 'createdAt',
    USER_ID: 'userId',
    STATUS: 'status'
  },
  
  // Document status values
  STATUS: {
    SUBMITTED: 'submitted',
    APPROVED: 'approved',
    REJECTED: 'rejected'
  }
};

/**
 * AI Verification constants
 */
export const AI_VERIFICATION = {
  // Spam detection thresholds
  SPAM_SCORE_THRESHOLD: 7,
  
  // Response field names
  FIELDS: {
    APPROVED: 'approved',
    FEEDBACK: 'feedback',
    SPAM_SCORE: 'spamScore',
    IS_BOT_GENERATED: 'isBotGenerated'
  }
};

/**
 * UI Colors - Category and component styling
 * Hex color mappings for backgrounds, borders, and rings
 */
export const UI_COLORS = {
  GUIDEBOOK_CATEGORIES: {
    'getting-started': { bgColor: '#dbeafe', borderColor: '#93c5fd', ringColor: '#3b82f6' },
    'configuration': { bgColor: '#f3e8ff', borderColor: '#d8b4fe', ringColor: '#a855f7' },
    'troubleshooting': { bgColor: '#fef3c7', borderColor: '#fcd34d', ringColor: '#f59e0b' },
    'features': { bgColor: '#cffafe', borderColor: '#a5f3fc', ringColor: '#06b6d4' },
    'issues': { bgColor: '#fbcfe8', borderColor: '#f472b6', ringColor: '#ec4899' },
    'account-billing': { bgColor: '#e0e7ff', borderColor: '#c7d2fe', ringColor: '#4f46e5' },
    'overview': { bgColor: '#dbeafe', borderColor: '#93c5fd', ringColor: '#3b82f6' },
    'tutorials': { bgColor: '#fed7aa', borderColor: '#fb923c', ringColor: '#f97316' },
    'best-practices': { bgColor: '#e0e7ff', borderColor: '#c7d2fe', ringColor: '#4f46e5' },
    'integrations': { bgColor: '#fbcfe8', borderColor: '#f472b6', ringColor: '#ec4899' }
  },
  
  DEFAULT_CATEGORY: { bgColor: '#f0f9ff', borderColor: '#bfdbfe', ringColor: '#3b82f6' },
  
  // Product type colors - used for category borders, glows, and styling
  PRODUCT_TYPES: {
    'mobile': {
      hex: '#eab308',
      rgba: 'rgba(234, 179, 8, 0.4)',
      border: '2px solid #eab308',
      bgLight: '#fef9e7'  // Very light yellow background
    },
    'web': {
      hex: '#22d3ee',
      rgba: 'rgba(34, 211, 238, 0.4)',
      border: '2px solid #22d3ee',
      bgLight: '#ecf8fc'  // Very light cyan background
    },
    'desktop': {
      hex: '#ec4899',
      rgba: 'rgba(236, 72, 153, 0.4)',
      border: '2px solid #ec4899',
      bgLight: '#fce7f3'  // Very light pink background
    }
  }
};

/**
 * Placeholder Images
 * Fallback placeholder URLs for product thumbnails when images are missing
 * Uses placehold.co for dynamic placeholder generation
 */
export const PLACEHOLDERS = {
  // Product thumbnail sizes
  PRODUCT_THUMBNAIL_SMALL: 'https://placehold.co/96x96/EFEFEF/999999?text=P&font=inter',   // Used in product cards and rails
  PRODUCT_THUMBNAIL_LARGE: 'https://placehold.co/300x300/EFEFEF/999999?text=P&font=inter'  // Used in product detail view
};

/**
 * Cloud Functions and Backend Services
 */
export const CLOUD_FUNCTIONS = {
  // Function names
  NAMES: {
    VERIFY_IDEA: 'verifyIdea',
    GET_ADMIN_EMAIL: 'getAdminEmail',
    GET_OFFICE_EMAIL: 'getOfficeEmail'
  },
  
  // Default function for error handling
  DEFAULT_ERROR_FUNCTION: 'getAdminEmail'
};

/**
 * Service Worker and Caching
 */
export const CACHE = {
  // Cache name for versioning
  NAME: 'engryv-site-images-v2',
  
  // Image assets to cache for offline support
  IMAGE_ASSETS: [
    '/images/EngryvLogo_small.png',
    '/images/EngryvLogo_mobile_small.png',
    '/images/Engryv_toolboss.png',
    '/images/espy/Engryv_under_construction_small.png',
    '/images/ideas/Engryv_ideas_small.png',
    '/images/pejas/pejas_chat_private.png',
    '/images/pejas/pejas_chat.png',
    '/images/pejas/pejas_login.png',
    '/images/pejas/pejas_pricing_plans.png',
    '/images/pejas/pejas_settings.png',
    '/images/pejas/pejas_thumb.png'
  ]
};

/**
 * Local Storage Keys
 * Centralized localStorage key names to prevent typos and enable safe refactoring
 */
export const STORAGE_KEYS = {
  // Cookie preferences
  COOKIE_CONSENT: 'cookieConsent', // 'accepted' | 'settings' | null
};

/**
 * Analytics Event Names
 * Standard event names for Firebase Analytics integration
 * Provides consistent event tracking across the application
 */
export const ANALYTICS_EVENTS = {
  // Navigation events
  VIEW_NAVIGATION: 'view_navigation', // When user navigates to a view/route
  PAGE_VIEW: 'page_view',              // Standard page view event
  
  // Idea submission events
  IDEA_SUBMISSION_START: 'idea_submission_start',
  IDEA_SUBMISSION_SUCCESS: 'idea_submission_success',
  IDEA_SUBMISSION_FAILURE: 'idea_submission_failure',
  IDEA_VERIFICATION_START: 'idea_verification_start',
  
  // Product interaction events
  PRODUCT_VIEW: 'product_view',
  PRODUCT_RAIL_EXPAND: 'product_rail_expand',
  
  // Guidebook interaction events
  GUIDEBOOK_SEARCH: 'guidebook_search',
  GUIDEBOOK_CATEGORY_FILTER: 'guidebook_category_filter',
  GUIDEBOOK_ARTICLE_VIEW: 'guidebook_article_view',
  
  // Email reveal events
  EMAIL_REVEAL_ATTEMPT: 'email_reveal_attempt',
  EMAIL_REVEAL_SUCCESS: 'email_reveal_success',
  EMAIL_REVEAL_FAILURE: 'email_reveal_failure',
  
  // Cookie consent events
  COOKIE_CONSENT_ACCEPTED: 'cookie_consent_accepted',
  COOKIE_CONSENT_SETTINGS: 'cookie_consent_settings',
  COOKIE_CONSENT_DECLINED: 'cookie_consent_declined',
  
  // Language change events
  LANGUAGE_CHANGE: 'language_change',
  
  // Error tracking events
  ERROR_OCCURRED: 'error_occurred',
  AI_VERIFICATION_REJECTED: 'ai_verification_rejected'
};

/**
 * i18n Translation Key Patterns
 * Documents the structure and conventions for translation keys
 * Used for reference and documentation of available translations
 */
export const I18N_KEYS = {
  // Navigation section
  NAV: {
    HOME: 'nav.home',
    PRODUCTS: 'nav.products',
    GUIDEBOOK: 'nav.guidebook',
    GUIDE: 'nav.guide',
    FAQS: 'nav.faqs',
    ABOUT: 'nav.about',
    CONTACT: 'nav.contact'
  },
  
  // Footer section
  FOOTER: {
    PRIVACY_POLICY: 'footer.privacyPolicy',
    TERMS_OF_SERVICE: 'footer.termsOfService',
    COOKIE_SETTINGS: 'footer.cookieSettings',
    COPYRIGHT: 'footer.copyright'
  },
  
  // Cookie section
  COOKIE: {
    BANNER_TEXT: 'cookie.bannerText',
    ACCEPT: 'cookie.accept',
    SETTINGS: 'cookie.settings',
    SETTINGS_TITLE: 'cookie.settingsTitle',
    SETTINGS_DESCRIPTION: 'cookie.settingsDescription',
    NECESSARY: 'cookie.necessary',
    NECESSARY_DESC: 'cookie.necessaryDesc',
    ANALYTICS_LABEL: 'cookie.analyticsLabel',
    SAVE_BUTTON: 'cookie.saveButton',
    LAST_UPDATED: 'cookie.lastUpdated'
  },
  
  // Idea form section
  IDEA_FORM: {
    WORD_LIMIT: 'ideaForm.wordLimit',
    FILL_OUT_BOTH: 'ideaForm.fillOutBoth',
    VERIFYING_AI: 'ideaForm.verifyingAI',
    CHECKING_EXISTING: 'ideaForm.checkingExisting',
    ONE_PER_DAY: 'ideaForm.onePerDay',
    SUBMITTING: 'ideaForm.submitting',
    THANK_YOU: 'ideaForm.thankYou',
    SUBMIT_ERROR: 'ideaForm.submitError'
  },
  
  // Product detail section
  PRODUCT_DETAIL: {
    AVAILABILITY: 'productDetail.availability',
    CREDITS: 'productDetail.credits',
    NO_IMAGE_DESC: 'productDetail.noImageDesc',
    LOADING: 'productDetail.loading',
    NOT_FOUND: 'productDetail.notFound',
    PRODUCT_NAME_MISSING: 'productDetail.productNameMissing',
    NO_DESCRIPTION: 'productDetail.noDescription'
  }
};

export default {
  FORM,
  ERROR,
  FIRESTORE,
  AI_VERIFICATION,
  UI_COLORS,
  PLACEHOLDERS,
  CLOUD_FUNCTIONS,
  CACHE,
  STORAGE_KEYS,
  ANALYTICS_EVENTS,
  I18N_KEYS
};

/**
 * Utility function to get product type colors
 * @param {string} productType - 'mobile', 'web', or 'desktop'
 * @returns {object} Color object with hex, rgba, and border properties
 */
export function getProductTypeColor(productType) {
  return UI_COLORS.PRODUCT_TYPES[productType] || UI_COLORS.PRODUCT_TYPES.mobile;
}
